import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/* Build an embeddable player URL for Vimeo / YouTube links; returns null for direct files. */
const getEmbedUrl = (src) => {
  if (!src) return null;
  // Vimeo — supports optional privacy hash: vimeo.com/ID or vimeo.com/ID/HASH
  const vimeo = src.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/);
  if (vimeo) {
    const id = vimeo[1];
    const hash = vimeo[2];
    const params = new URLSearchParams({ autoplay: "1", title: "0", byline: "0", portrait: "0", dnt: "1", app_id: "58479" });
    if (hash) params.set("h", hash);
    return `https://player.vimeo.com/video/${id}?${params.toString()}`;
  }
  // YouTube — youtu.be/ID or youtube.com/watch?v=ID
  const yt = src.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (yt) {
    return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0&modestbranding=1`;
  }
  return null;
};

/* Lightbox modal for the sound-on conversion film. Accessible: focus trap-lite, Esc to close. */
export const FilmModal = ({ open, onClose, src }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const embedUrl = getEmbedUrl(src);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          role="dialog" aria-modal="true" aria-label="Conversion film"
          data-testid="film-modal"
        >
          <div className="absolute inset-0 bg-[#050c16]/90 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="relative w-full max-w-5xl aspect-video bg-black rounded-sm overflow-hidden border border-hair-dark"
            initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              onClick={onClose}
              data-testid="film-modal-close"
              aria-label="Close film"
              className="absolute top-3 right-3 z-10 p-2 text-white/80 hover:text-white bg-black/40 rounded-sm focus-ring"
            >
              <X className="h-5 w-5" />
            </button>
            {embedUrl ? (
              <iframe
                className="h-full w-full"
                src={embedUrl}
                title="Operations Transformation Program film"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video className="h-full w-full object-contain bg-black" controls autoPlay playsInline src={src} />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
